import { useCallback, useEffect, useState } from 'react'
import { DateField } from './DatePicker'
import { adminRequest } from './EshopCategories'

type Bucket = { key: string; label: string; totalCents: number; orders: number }
type GroupRow = { key: string; label: string; totalCents: number; orders: number }
type ProductRow = { name: string; qty: number; totalCents: number }
type CategoryRow = { category: string; qty: number; totalCents: number }
type Analytics = {
  period: { from: string; to: string; days: number; granularity: 'day' | 'month' }
  revenue: {
    totalCents: number; orders: number; aovCents: number
    prevTotalCents: number; prevOrders: number; prevAovCents: number
    changePercent: number | null; ordersChangePercent: number | null; aovChangePercent: number | null
  }
  series: Bucket[]
  byDeliveryMethod: GroupRow[]
  byPaymentMethod: GroupRow[]
  topProducts: ProductRow[]
  topCategories: CategoryRow[]
  customers: {
    newOrders: number; returningOrders: number; newRevenueCents: number; returningRevenueCents: number
    newCustomers: number; returningCustomers: number; repeatRatePercent: number
  }
}

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const isoDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava' }).format(date)
const addDays = (iso: string, days: number) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return isoDay(d) }
const pct = (value: number | null) => value === null ? '—' : `${value > 0 ? '+' : ''}${value.toLocaleString('sk-SK', { maximumFractionDigits: 1 })} %`
const trendTone = (value: number | null) => value === null || value === 0 ? '' : value > 0 ? 'is-up' : 'is-down'

const PRESETS: Array<{ label: string; range: () => { from: string; to: string } }> = [
  { label: '7 dní', range: () => ({ from: addDays(isoDay(new Date()), -6), to: isoDay(new Date()) }) },
  { label: '30 dní', range: () => ({ from: addDays(isoDay(new Date()), -29), to: isoDay(new Date()) }) },
  { label: '90 dní', range: () => ({ from: addDays(isoDay(new Date()), -89), to: isoDay(new Date()) }) },
  { label: 'Tento mesiac', range: () => { const n = new Date(); return { from: isoDay(new Date(n.getFullYear(), n.getMonth(), 1)), to: isoDay(n) } } },
  { label: 'Minulý mesiac', range: () => { const n = new Date(); const start = new Date(n.getFullYear(), n.getMonth() - 1, 1); const end = new Date(n.getFullYear(), n.getMonth(), 0); return { from: isoDay(start), to: isoDay(end) } } },
  { label: 'Tento rok', range: () => { const n = new Date(); return { from: isoDay(new Date(n.getFullYear(), 0, 1)), to: isoDay(n) } } },
]

export default function StatsAdmin() {
  const [from, setFrom] = useState(() => addDays(isoDay(new Date()), -29))
  const [to, setTo] = useState(() => isoDay(new Date()))
  const [activePreset, setActivePreset] = useState('30 dní')
  const [data, setData] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const result = await adminRequest(`/api/admin/analytics?from=${from}&to=${to}`)
      setData(result)
    } catch (e) { setError(e instanceof Error ? e.message : 'Štatistiky sa nepodarilo načítať.') } finally { setLoading(false) }
  }, [from, to])
  useEffect(() => { void load() }, [load])

  const applyPreset = (preset: typeof PRESETS[number]) => {
    const { from: f, to: t } = preset.range()
    setFrom(f); setTo(t); setActivePreset(preset.label)
  }

  const maxBucket = Math.max(1, ...(data?.series ?? []).map(b => b.totalCents))

  return <div className="panel stats-admin">
    <div className="panel-heading"><div><h2>Štatistiky</h2><p>Tržby, výkon produktov a zákazníci za zvolené obdobie.</p></div>
      <div className="pa-head-actions"><button className="text-btn" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button></div>
    </div>

    <div className="sk-filter-row st-filters">
      <div className="st-presets">{PRESETS.map(preset => <button key={preset.label} type="button" className={`pa-chip ${activePreset === preset.label ? 'is-on' : ''}`} onClick={() => applyPreset(preset)}>{preset.label}</button>)}</div>
      <div className="st-dates">
        <DateField label="Od" value={from} onChange={v => { setFrom(v); setActivePreset('') }} clearable={false} max={to} />
        <DateField label="Do" value={to} onChange={v => { setTo(v); setActivePreset('') }} clearable={false} min={from} />
      </div>
    </div>

    {error && <p className="error-message">{error}</p>}
    {loading && !data ? <p className="empty-message">Načítavam štatistiky…</p> : !data ? null : <>
      <div className="st-tiles">
        <div className="st-tile">
          <span>Tržby</span><strong>{euro(data.revenue.totalCents)}</strong>
          <small className={trendTone(data.revenue.changePercent)}>{pct(data.revenue.changePercent)} oproti predošlému obdobiu</small>
        </div>
        <div className="st-tile">
          <span>Objednávky</span><strong>{data.revenue.orders}</strong>
          <small className={trendTone(data.revenue.ordersChangePercent)}>{pct(data.revenue.ordersChangePercent)} oproti predošlému obdobiu</small>
        </div>
        <div className="st-tile">
          <span>Priemerná objednávka</span><strong>{euro(data.revenue.aovCents)}</strong>
          <small className={trendTone(data.revenue.aovChangePercent)}>{pct(data.revenue.aovChangePercent)} oproti predošlému obdobiu</small>
        </div>
        <div className="st-tile">
          <span>Vracajúci sa zákazníci</span><strong>{data.customers.repeatRatePercent.toLocaleString('sk-SK', { maximumFractionDigits: 1 })} %</strong>
          <small>{data.customers.returningCustomers} vracajúcich / {data.customers.newCustomers} nových</small>
        </div>
      </div>

      <section className="panel db-card st-chart-card">
        <header className="db-card-head"><h3>Tržby v čase</h3><span className="pa-muted">{data.period.granularity === 'day' ? 'podľa dňa' : 'podľa mesiaca'} · bez zrušených a odmietnutých</span></header>
        <div className="db-chart st-chart" role="img" aria-label="Tržby za zvolené obdobie">
          {data.series.map(b => <div key={b.key} className="db-bar" title={`${b.label}: ${euro(b.totalCents)} · ${b.orders} objednávok`}>
            <span className="db-bar-value">{b.orders || ''}</span>
            <span className="db-bar-fill" style={{ height: `${Math.max(b.totalCents ? 4 : 1, Math.round(b.totalCents / maxBucket * 100))}%` }} />
            <span className="db-bar-label">{b.label}</span>
          </div>)}
          {!data.series.length && <p className="empty-message">V tomto období nie sú žiadne objednávky.</p>}
        </div>
      </section>

      <div className="st-grid-2">
        <section className="panel db-card">
          <header className="db-card-head"><h3>Najpredávanejšie produkty</h3><span className="pa-muted">podľa tržieb</span></header>
          {!data.topProducts.length ? <p className="empty-message">Zatiaľ žiadne predané produkty.</p> :
            <ol className="st-rank">{data.topProducts.map(p => <li key={p.name}>
              <span className="st-rank-name">{p.name}</span>
              <span className="st-rank-qty">{p.qty.toLocaleString('sk-SK')} ks</span>
              <b>{euro(p.totalCents)}</b>
            </li>)}</ol>}
        </section>
        <section className="panel db-card">
          <header className="db-card-head"><h3>Kategórie</h3><span className="pa-muted">podľa tržieb</span></header>
          {!data.topCategories.length ? <p className="empty-message">Zatiaľ žiadne predané produkty.</p> :
            <ol className="st-rank">{data.topCategories.map(c => <li key={c.category}>
              <span className="st-rank-name">{c.category}</span>
              <span className="st-rank-qty">{c.qty.toLocaleString('sk-SK')} ks</span>
              <b>{euro(c.totalCents)}</b>
            </li>)}</ol>}
        </section>
      </div>

      <div className="st-grid-2">
        <section className="panel db-card">
          <header className="db-card-head"><h3>Spôsob doručenia</h3></header>
          <ol className="st-rank">{data.byDeliveryMethod.map(r => <li key={r.key}>
            <span className="st-rank-name">{r.label}</span>
            <span className="st-rank-qty">{r.orders} objednávok</span>
            <b>{euro(r.totalCents)}</b>
          </li>)}</ol>
        </section>
        <section className="panel db-card">
          <header className="db-card-head"><h3>Spôsob platby</h3></header>
          <ol className="st-rank">{data.byPaymentMethod.map(r => <li key={r.key}>
            <span className="st-rank-name">{r.label}</span>
            <span className="st-rank-qty">{r.orders} objednávok</span>
            <b>{euro(r.totalCents)}</b>
          </li>)}</ol>
        </section>
      </div>

      <section className="panel db-card">
        <header className="db-card-head"><h3>Noví vs. vracajúci sa zákazníci</h3><span className="pa-muted">podľa toho, či mali objednávku ešte pred týmto obdobím</span></header>
        <div className="st-cust-split">
          <div><span>Noví</span><strong>{data.customers.newOrders}</strong><small>objednávok · {euro(data.customers.newRevenueCents)}</small></div>
          <div><span>Vracajúci sa</span><strong>{data.customers.returningOrders}</strong><small>objednávok · {euro(data.customers.returningRevenueCents)}</small></div>
        </div>
      </section>
    </>}
  </div>
}
